import {
  Divider,
  VStack,
  Flex,
  HStack,
  Spacer,
  Stack,
  Box,
} from '@chakra-ui/layout';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Button,
  Tag,
  TagLabel,
  DrawerFooter,
  Skeleton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  useGetCreditFacilityMetricsQuery,
  useGetInvoiceByIdQuery,
  InvoiceDisbursementFragmentGQLType,
  useGetTAccountsQuery,
  InvoiceFragmentGQLType,
  InvoiceDisbursementItemFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  formatCurrencyAmount,
  percentageToPercentageLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { DisbursementNoticeDownloadButton } from '~/packages/invoices/ui/disbursements/InvoiceDisbursementDownloadButton';
import { MarkAsDisbursedButton } from '~/packages/invoices/ui/disbursements/MarkAsDisbursedButton';
import {
  getBankAccountDisplay,
  TAccountTypeToLabelMap,
} from '~/packages/invoices/ui/utils';

export const InvoiceDisbursementItems = ({
  invoice,
  disbursementItems,
  invoiceComponentIndex,
}: {
  invoice: InvoiceFragmentGQLType;
  disbursementItems: InvoiceDisbursementItemFragmentGQLType[];
  invoiceComponentIndex: number;
}) => {
  const { invoiceComponentsWithInvoiceItems, creditFacilityId } = invoice;
  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const { data: tAccountsData } = useGetTAccountsQuery({
    variables: {
      creditFacilityId,
    },
  });

  const groupedInvoiceItems = useMemo(() => {
    if (tAccountsData) {
      const accruedInterestCashInvoiceItems: {
        title: string;
        invoiceAmount: number;
        disbursementAmount: number;
      }[] = [];
      const otherInvoiceItems: {
        title: string;
        invoiceAmount: number;
        disbursementAmount: number;
      }[] = [];

      let totalInvoiceAmount = 0;
      let totalDisbursementAmount = 0;

      invoiceItems.forEach((invoiceItem) => {
        const tAccountDetails = tAccountsData.getTAccounts.find(
          (tAccount) => tAccount.id === invoiceItem.accountingAccountId,
        );
        const disbursmentItem = disbursementItems.find(
          (disbursementItem) =>
            disbursementItem.accountingAccount.tAccount ===
            invoiceItem.accountingAccount.tAccount,
        );

        if (tAccountDetails && disbursmentItem) {
          totalInvoiceAmount += invoiceItem.amount;
          totalDisbursementAmount += disbursmentItem.amount;

          if (
            tAccountDetails.tAccount === 'accruedInterest' &&
            !invoiceItem.capitalizedLoanId // this means its cash & not PIK (disbursements are only cash so we only want to show cash interest items)
          ) {
            accruedInterestCashInvoiceItems.push({
              title: 'Interest',
              invoiceAmount: invoiceItem.amount,
              disbursementAmount: disbursmentItem.amount,
            });
          } else {
            otherInvoiceItems.push({
              title:
                TAccountTypeToLabelMap[tAccountDetails.tAccount] ??
                _.startCase(tAccountDetails.tAccount),
              invoiceAmount: invoiceItem.amount,
              disbursementAmount: disbursmentItem.amount,
            });
          }
        }
      });

      return {
        accruedInterestCashInvoiceItems,
        otherInvoiceItems,
        totalInvoiceAmount,
        totalDisbursementAmount,
      };
    }

    return null;
  }, [disbursementItems, invoiceItems, tAccountsData]);

  if (groupedInvoiceItems === null || !tAccountsData) {
    return <Skeleton width="full" h="400px" />;
  }

  return (
    <VStack width="full" gap="1rem">
      {/* header row */}
      <HStack width="full" mr="0">
        <Box width="40%" />
        <Box width="30%">
          <Typography fontWeight="semibold">Invoice</Typography>
        </Box>
        <Box width="30%">
          <Typography fontWeight="semibold">Lender Share</Typography>
        </Box>
      </HStack>

      {/* non-interest rows */}
      <Divider w="100%" />
      {groupedInvoiceItems.otherInvoiceItems.map((invoiceItem) => {
        return (
          <HStack key={JSON.stringify(invoiceItem)} width="full" mr="0">
            <Box width="40%">
              <Typography variant="base-muted">{invoiceItem.title}</Typography>
            </Box>
            <Box width="30%">
              <Typography>
                {dollarNumberWithCommas(invoiceItem.invoiceAmount)}
              </Typography>
            </Box>
            <Box width="30%">
              <Typography>
                {dollarNumberWithCommas(invoiceItem.disbursementAmount)}
              </Typography>
            </Box>
          </HStack>
        );
      })}

      {/* accrued interest row */}
      {groupedInvoiceItems.accruedInterestCashInvoiceItems.map(
        (interestInvoiceItem) => {
          return (
            <HStack
              key={JSON.stringify(interestInvoiceItem)}
              width="full"
              mr="0"
            >
              <Box width="40%">
                <Typography variant="base-muted">
                  {interestInvoiceItem.title}
                </Typography>
              </Box>

              <Box width="30%">
                <Typography>
                  {dollarNumberWithCommas(interestInvoiceItem.invoiceAmount)}
                </Typography>
              </Box>

              <Box width="30%">
                <Typography>
                  {dollarNumberWithCommas(
                    interestInvoiceItem.disbursementAmount,
                  )}
                </Typography>
              </Box>
            </HStack>
          );
        },
      )}

      {/* total */}
      <Divider w="100%" />
      <HStack width="full" mr="0">
        <Box width="40%">
          <Typography variant="base-muted">Total</Typography>
        </Box>
        <Box width="30%">
          <Typography>
            {dollarNumberWithCommas(groupedInvoiceItems.totalInvoiceAmount)}
          </Typography>
        </Box>
        <Box width="30%">
          <Typography>
            {dollarNumberWithCommas(
              groupedInvoiceItems.totalDisbursementAmount,
            )}
          </Typography>
        </Box>
      </HStack>
    </VStack>
  );
};

export const InvoiceDisbursementDrawer = ({
  isOpen,
  invoiceDisbursement,
  facilityMechanics,
  onToggle,
}: {
  isOpen: boolean;
  invoiceDisbursement: InvoiceDisbursementFragmentGQLType;
  facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities;
  onToggle: (isOpen: boolean) => void;
}) => {
  const lenderCommitmentAmount = useMemo(() => {
    const capitalProviderEntity = facilityMechanics.parties.capitalProviders.find(
      (capProvider) =>
        capProvider.entity.id ===
        invoiceDisbursement.capitalProviderOrganization.id,
    );

    finleyAssert(
      capitalProviderEntity,
      `[InvoiceDisbursementDrawer] Missing capitalProviderEntity for ${_.startCase(
        invoiceDisbursement.capitalProviderOrganization.name,
      )}`,
    );

    return capitalProviderEntity.commitmentAmount;
  }, [
    facilityMechanics.parties.capitalProviders,
    invoiceDisbursement.capitalProviderOrganization.id,
    invoiceDisbursement.capitalProviderOrganization.name,
  ]);

  const invoiceQuery = useGetInvoiceByIdQuery({
    variables: {
      invoiceId: invoiceDisbursement.invoiceId,
    },
  });

  const reportDownload = useMemo(() => {
    return invoiceQuery.data?.getInvoiceById.invoiceReports?.find(
      (download) =>
        download.capitalProviderId ===
          invoiceDisbursement.capitalProviderOrganization.id &&
        download.reportType === 'disbursementNotice',
    );
  }, [invoiceDisbursement.capitalProviderOrganization.id, invoiceQuery.data]);

  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !invoiceQuery.data,
    variables: {
      creditFacilityId: invoiceQuery?.data?.getInvoiceById
        .creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  if (invoiceQuery.error) {
    throw new Error(
      `Failed to get Invoice for Disbursement. Please contact Finley support.`,
    );
  }

  if (!invoiceQuery.data?.getInvoiceById) {
    return null;
  }

  const invoice = invoiceQuery.data.getInvoiceById;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Funding Disbursement for{' '}
            {_.startCase(invoiceDisbursement.capitalProviderOrganization.name)}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <HStack justifyContent="space-between" w="100%">
              {reportDownload && (
                <DisbursementNoticeDownloadButton report={reportDownload} />
              )}
              <Spacer />
              {(() => {
                if (invoiceDisbursement.status === 'paid') {
                  return (
                    <Tag
                      variant="subtle"
                      colorScheme="green"
                      borderRadius="8px"
                      padding="8px 16px"
                    >
                      <Icon
                        icon="circle-check"
                        color={colors.green['300']}
                        style={{ marginRight: '8px' }}
                      />
                      <TagLabel>Paid</TagLabel>
                    </Tag>
                  );
                }
                return (
                  <MarkAsDisbursedButton
                    invoiceDisbursement={invoiceDisbursement}
                  />
                );
              })()}
            </HStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Net Funds Owed to{' '}
              {_.startCase(
                invoiceDisbursement.capitalProviderOrganization.name,
              )}
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Funds Owed to Lender
                </Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    invoiceDisbursement.totalAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Paid Date to Lender</Typography>
                <Typography variant="base-muted">
                  {invoiceDisbursement.paymentDate
                    ? commonDate.fullDateStrToHumanDateString(
                        invoiceDisbursement.paymentDate,
                      )
                    : '-'}
                </Typography>
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Disbursement Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Invoice Type</Typography>
                <Typography variant="base-muted">
                  {_.startCase(invoice.invoiceType)}
                </Typography>
              </HStack>
              {invoice.loan && (
                <HStack justifyContent="space-between" w="100%">
                  <Typography fontWeight="medium">Loan ID</Typography>
                  <Typography variant="base-muted">
                    {truncateUuid(invoice.loan.id)}
                  </Typography>
                </HStack>
              )}
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Invoice ID</Typography>
                <Typography variant="base-muted">
                  {truncateUuid(invoice.id)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Paid Date</Typography>
                <Typography variant="base-muted">
                  {invoiceDisbursement.paymentDate // is this not the same as Paid Date to Lender
                    ? commonDate.fullDateStrToHumanDateString(
                        invoiceDisbursement.paymentDate,
                      )
                    : '-'}
                </Typography>
              </HStack>
              <HStack align="top" w="100%" gap="1em">
                <Typography fontWeight="medium">
                  Line Items for{' '}
                  {commonDate.fullDateStrToHumanDateString(
                    invoice.accrualStartDate,
                  )}{' '}
                  -{' '}
                  {commonDate.fullDateStrToHumanDateString(
                    invoice.accrualEndDate,
                  )}
                </Typography>
                <Spacer />
                {invoice.invoiceComponentsWithInvoiceItems.map(
                  (
                    _invoiceComponentWithInvoiceItems,
                    invoiceComponentIndex,
                  ) => {
                    const key = invoiceComponentIndex;
                    return (
                      <InvoiceDisbursementItems
                        key={key}
                        invoice={invoice}
                        disbursementItems={
                          invoiceDisbursement.invoiceDisbursementItems
                        }
                        invoiceComponentIndex={invoiceComponentIndex}
                      />
                    );
                  },
                )}
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Facility Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Name</Typography>
                <Typography variant="base-muted">
                  {_.startCase(invoice.creditFacility.name)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Borrower</Typography>
                <Typography variant="base-muted">
                  {_.startCase(invoice.paymentSourceOrganization.name)}
                </Typography>
              </HStack>
              {invoice.loan && (
                <HStack justifyContent="space-between" w="100%">
                  <Typography fontWeight="medium">Global Commitment</Typography>
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      invoice.loan.facility.commitmentAmount,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                </HStack>
              )}
              <HStack alignItems="start" w="100%">
                <Typography fontWeight="medium">
                  {_.startCase(
                    invoiceDisbursement.capitalProviderOrganization.name,
                  )}{' '}
                  Commitment
                </Typography>

                <Spacer />
                <Stack alignItems="end">
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      lenderCommitmentAmount.value,
                      lenderCommitmentAmount.currency,
                    )}
                  </Typography>
                  {invoice.loan && (
                    <Typography variant="base-muted">
                      (
                      {percentageToPercentageLabel(
                        (lenderCommitmentAmount.value /
                          invoice.loan.facility.commitmentAmount) *
                          100,
                        6,
                      )}{' '}
                      Pro Rata)
                    </Typography>
                  )}
                </Stack>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Global Outstanding Balance
                </Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Payment Instructions
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Destination Bank Account
                </Typography>
                <Typography variant="base-muted">
                  {invoice.destinationBankAccount
                    ? getBankAccountDisplay({
                        institutionName:
                          invoice.destinationBankAccount.institutionName,
                        accountName: invoice.destinationBankAccount.name,
                        accountNumber:
                          invoice.destinationBankAccount.accountNumber,
                      })
                    : '-'}
                </Typography>
              </HStack>
            </VStack>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <Flex justify="flex-end" w="100%" mb="2em">
            <Button
              as={RouterLink}
              to={`/invoices/${invoice.id}`}
              variant="v2-ghost"
              rightIcon={<Icon icon="chevron-right" />}
            >
              View Invoice
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
